import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Receipt, Euro, ShoppingBag, TrendingUp,
  RefreshCw, ChevronLeft, ChevronRight, Table2, CalendarRange, X, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import ReceiptTicketModal from './LiveTables/ReceiptTicketModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODOS = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'ayer', label: 'Ayer' },
  { value: 'semana', label: 'Última semana' },
  { value: 'mes', label: 'Último mes' },
  { value: 'año', label: 'Último año' },
];

const DAY_LABEL_RX = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

const SalesHistory = () => {
  const [ventas, setVentas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketSale, setTicketSale] = useState(null);
  const [periodo, setPeriodo] = useState('hoy');
  // Custom date range — `*Draft` is what the user is editing in the inputs,
  // `*Applied` is what the fetch actually uses. This avoids a fetch on every
  // keystroke / partial date typed.
  const [dateFromDraft, setDateFromDraft] = useState('');
  const [dateToDraft, setDateToDraft] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit: 100 });
  // Collapsed/expanded state per day group
  const [collapsedDays, setCollapsedDays] = useState({});

  const customRangeActive = Boolean(dateFrom || dateTo);

  const fetchVentas = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pagination.limit));
      params.set('offset', String(pagination.offset));
      if (dateFrom) params.set('start_date', `${dateFrom}T00:00:00`);
      if (dateTo) params.set('end_date', `${dateTo}T23:59:59`);
      const response = await fetch(`${API_URL}/api/admin/ventas?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVentas(data.ventas);
        setPagination(prev => ({ ...prev, total: data.total }));
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, dateFrom, dateTo]);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/ventas/stats?periodo=${encodeURIComponent(periodo)}${dateFrom ? `&fecha_inicio=${dateFrom}` : ''}${dateTo ? `&fecha_fin=${dateTo}` : ''}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [periodo, dateFrom, dateTo]);

  useEffect(() => { fetchVentas(); }, [fetchVentas]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDayHeader = (isoDay) => {
    // isoDay = 'YYYY-MM-DD'
    try {
      const d = new Date(`${isoDay}T12:00:00`);
      const weekday = DAY_LABEL_RX[d.getDay()];
      const dayMonth = d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
      return `${weekday} · ${dayMonth}`;
    } catch { return isoDay; }
  };

  // Group sales by day (YYYY-MM-DD), keep order desc by date.
  const groupedByDay = useMemo(() => {
    const groups = new Map();
    for (const v of ventas) {
      let dayKey;
      try {
        dayKey = new Date(v.created_at).toISOString().slice(0, 10);
      } catch {
        dayKey = (v.created_at || '').slice(0, 10) || 'sin-fecha';
      }
      if (!groups.has(dayKey)) groups.set(dayKey, []);
      groups.get(dayKey).push(v);
    }
    // Convert to array sorted by day desc
    const arr = Array.from(groups.entries())
      .map(([day, sales]) => {
        const total = sales.reduce((s, x) => s + (x.total_amount || 0), 0);
        const items = sales.reduce((s, x) => s + (x.item_count || 0), 0);
        return { day, sales, total, items, count: sales.length };
      })
      .sort((a, b) => (a.day < b.day ? 1 : -1));
    return arr;
  }, [ventas]);

  const toggleDay = (day) => {
    setCollapsedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const openTicket = (venta) => {
    setTicketSale({
      sale_id: venta.id,
      mesa_numero: venta.mesa_numero ?? 0,
      total: venta.total_amount,
      items: venta.items || [],
      payment_method: venta.payment_method || 'efectivo',
      closed_at: venta.closed_at || venta.created_at
    });
  };

  const clearDateRange = () => {
    setDateFromDraft('');
    setDateToDraft('');
    setDateFrom('');
    setDateTo('');
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const applyDateRange = () => {
    setDateFrom(dateFromDraft);
    setDateTo(dateToDraft);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Historial de Ventas</h2>
          <p className="text-gray-500">Registro de todas las ventas completadas</p>
        </div>
        <Button onClick={() => { fetchVentas(); fetchStats(); }} variant="outline" className="flex items-center gap-2" data-testid="sales-refresh-btn">
          <RefreshCw size={16} />
          Actualizar
        </Button>
      </div>

      {/* Period selector */}
      <div className="flex gap-2 flex-wrap">
        {PERIODOS.map(p => (
          <Button
            key={p.value}
            variant={periodo === p.value && !customRangeActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setPeriodo(p.value); setDateFrom(''); setDateTo(''); setDateFromDraft(''); setDateToDraft(''); }}
            className={periodo === p.value && !customRangeActive ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid={`period-btn-${p.value}`}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {/* Stats Cards (always reflect period selector — quick KPIs) */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><Euro className="text-green-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_ingresos}€</p>
                  <p className="text-sm text-gray-500">Ingresos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Receipt className="text-blue-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_ventas}</p>
                  <p className="text-sm text-gray-500">Ventas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><TrendingUp className="text-amber-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.ticket_medio}€</p>
                  <p className="text-sm text-gray-500">Ticket medio</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><ShoppingBag className="text-purple-600" size={20} /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_items}</p>
                  <p className="text-sm text-gray-500">Items vendidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Products */}
      {stats?.top_products && stats.top_products.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Productos más vendidos ({customRangeActive ? `${dateFrom || '…'} → ${dateTo || 'hoy'}` : PERIODOS.find(p => p.value === periodo)?.label})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_products.slice(0, 5).map((product, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                    <span className="font-medium">{product.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-green-600">{product.revenue.toFixed(2)}€</span>
                    <span className="text-sm text-gray-500 ml-2">({product.quantity} uds)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">Historial agrupado por día</CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{pagination.total === 0 ? '0' : pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} de {pagination.total}</span>
                <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={pagination.offset === 0}>
                  <ChevronLeft size={16} />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNextPage} disabled={pagination.offset + pagination.limit >= pagination.total}>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
            {/* Custom date range */}
            <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg bg-gray-50 border border-gray-100">
              <CalendarRange size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Buscar por fecha:</span>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Desde</label>
                <Input
                  type="date"
                  value={dateFromDraft}
                  onChange={(e) => setDateFromDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyDateRange(); }}
                  className="h-9 w-[150px]"
                  data-testid="sales-date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Hasta</label>
                <Input
                  type="date"
                  value={dateToDraft}
                  onChange={(e) => setDateToDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyDateRange(); }}
                  className="h-9 w-[150px]"
                  data-testid="sales-date-to"
                />
              </div>
              <Button size="sm" onClick={applyDateRange} className="bg-green-600 hover:bg-green-700" data-testid="sales-apply-range-btn">
                Aplicar
              </Button>
              {customRangeActive && (
                <Button size="sm" variant="outline" onClick={clearDateRange} className="text-gray-600" data-testid="sales-clear-range-btn">
                  <X size={14} className="mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-gray-400" size={28} />
            </div>
          ) : groupedByDay.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Receipt size={48} className="mx-auto mb-4 opacity-50" />
              <p>No hay ventas en este rango</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="sales-grouped-list">
              {groupedByDay.map((group) => {
                const isCollapsed = !!collapsedDays[group.day];
                return (
                  <div key={group.day} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                    {/* Day header */}
                    <button
                      type="button"
                      onClick={() => toggleDay(group.day)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:from-green-50 hover:to-white transition-colors"
                      data-testid={`day-header-${group.day}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isCollapsed
                          ? <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
                          : <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />}
                        <div className="text-left">
                          <p className="font-semibold text-gray-900 capitalize">{formatDayHeader(group.day)}</p>
                          <p className="text-xs text-gray-500">{group.count} ventas · {group.items} items</p>
                        </div>
                      </div>
                      <p className="text-xl font-extrabold text-green-600 whitespace-nowrap">{group.total.toFixed(2)}€</p>
                    </button>

                    {/* Day sales */}
                    {!isCollapsed && (
                      <div className="divide-y divide-gray-100">
                        {group.sales.map((venta) => (
                          <div
                            key={venta.id}
                            className="px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                            data-testid={`sale-row-${venta.id}`}
                          >
                            <div className="p-1.5 rounded-md bg-white border border-gray-100 flex-shrink-0">
                              <Table2 size={16} className="text-gray-500" />
                            </div>
                            <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">Mesa {venta.mesa_numero || '—'}</p>
                                <p className="text-[11px] text-gray-400">{formatTime(venta.created_at)}{venta.duration_minutes ? ` · ${formatDuration(venta.duration_minutes)}` : ''}</p>
                              </div>
                              <div className="text-xs text-gray-500 capitalize truncate">{venta.payment_method || 'efectivo'}</div>
                              <div className="text-xs text-gray-500">{venta.item_count} items</div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-green-600">{venta.total_amount.toFixed(2)}€</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openTicket(venta)}
                              className="ml-2 flex-shrink-0 border-gray-300 hover:bg-gray-900 hover:text-white"
                              data-testid={`view-ticket-btn-${venta.id}`}
                            >
                              <Receipt size={14} className="mr-1" />
                              Ver
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket modal (reusable, with email-to-client) */}
      {ticketSale && (
        <ReceiptTicketModal saleData={ticketSale} onClose={() => setTicketSale(null)} />
      )}
    </div>
  );
};

export default SalesHistory;
